/**
 * Chart geometry.
 *
 * Every function here returns FRACTIONS (0–1) or percentages, never pixels.
 * That is what lets the same numbers drive HTML marks, a stretched SVG plot and
 * absolutely-positioned labels without any text measurement: the browser does
 * the layout, this module only says where things sit proportionally.
 *
 * Mirrored by Chart\ChartGeometry in PHP. Change both together.
 */

/**
 * Round a range endpoint to a human number (1, 2, 5 × a power of ten).
 *
 * @param {number}  range Span to round.
 * @param {boolean} round Round to nearest rather than up.
 * @return {number} Rounded span.
 */
function niceNum( range, round ) {
	const exponent = Math.floor( Math.log10( range ) );
	const fraction = range / Math.pow( 10, exponent );
	let nice;

	if ( round ) {
		nice = fraction < 1.5 ? 1 : fraction < 3 ? 2 : fraction < 7 ? 5 : 10;
	} else {
		nice = fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 5 ? 5 : 10;
	}

	return nice * Math.pow( 10, exponent );
}

/**
 * Build an axis scale whose ticks land on round numbers.
 *
 * @param {number} min       Data minimum.
 * @param {number} max       Data maximum.
 * @param {number} tickCount Desired tick count.
 * @return {{min: number, max: number, step: number, ticks: number[]}} Scale.
 */
export function niceScale( min, max, tickCount = 5 ) {
	const count = Math.max( 2, tickCount );

	// A flat series has no span to divide; give it one so the axis still reads.
	if ( max === min ) {
		if ( max === 0 ) {
			max = 1;
		} else {
			min = Math.min( 0, max );
			max = Math.max( 0, max );
			if ( max === min ) {
				max = min + 1;
			}
		}
	}

	const step = niceNum( niceNum( max - min, false ) / ( count - 1 ), true );
	const niceMin = Math.floor( min / step ) * step;
	const niceMax = Math.ceil( max / step ) * step;
	const ticks = [];

	// Accumulating with += drifts on fractional steps; multiply instead.
	const steps = Math.round( ( niceMax - niceMin ) / step );
	for ( let i = 0; i <= steps; i++ ) {
		ticks.push( roundFloat( niceMin + i * step ) );
	}

	return { min: niceMin, max: niceMax, step, ticks };
}

/**
 * Trim the float noise that `min + i * step` leaves behind (0.30000000000000004).
 *
 * @param {number} value Value to clean.
 * @return {number} Cleaned value.
 */
function roundFloat( value ) {
	return parseFloat( value.toPrecision( 12 ) );
}

/**
 * Resolve the value axis for a set of series.
 *
 * Bars, areas and stacks always include zero — a bar whose baseline is not zero
 * misstates its own length. A plain line may float, because a line of values
 * 100–110 against a zero baseline shows nothing.
 *
 * @param {Array}  series    Series objects with a `data` array.
 * @param {Object} options   Scale options.
 * @return {{min: number, max: number, step: number, ticks: number[]}} Scale.
 */
export function resolveScale( series, options = {} ) {
	const {
		stacked = false,
		zeroBased = true,
		tickCount = 5,
		axisMin = null,
		axisMax = null,
	} = options;

	const values = [];

	if ( stacked ) {
		// A stack's height is the sum at that category, not any single value.
		const length = Math.max(
			0,
			...series.map( ( s ) => ( s.data || [] ).length )
		);
		for ( let i = 0; i < length; i++ ) {
			let positive = 0;
			let negative = 0;
			series.forEach( ( s ) => {
				const value = toNumber( ( s.data || [] )[ i ] );
				if ( value < 0 ) {
					negative += value;
				} else {
					positive += value;
				}
			} );
			values.push( positive, negative );
		}
	} else {
		series.forEach( ( s ) =>
			( s.data || [] ).forEach( ( value ) =>
				values.push( toNumber( value ) )
			)
		);
	}

	if ( ! values.length ) {
		values.push( 0, 1 );
	}

	let min = Math.min( ...values );
	let max = Math.max( ...values );

	if ( zeroBased ) {
		min = Math.min( 0, min );
		max = Math.max( 0, max );
	}

	const scale = niceScale( min, max, tickCount );

	// A manual bound replaces the computed one but keeps the round tick steps.
	if ( axisMin !== null && axisMin !== '' && isFinite( axisMin ) ) {
		scale.min = Number( axisMin );
	}
	if ( axisMax !== null && axisMax !== '' && isFinite( axisMax ) ) {
		scale.max = Number( axisMax );
	}
	if ( axisMin !== null || axisMax !== null ) {
		scale.ticks = scale.ticks.filter(
			( tick ) => tick >= scale.min && tick <= scale.max
		);
		if ( ! scale.ticks.length ) {
			scale.ticks = [ scale.min, scale.max ];
		}
	}

	return scale;
}

/**
 * Position of a value on the axis, as a fraction from the axis minimum.
 *
 * @param {number} value Value.
 * @param {Object} scale Scale from resolveScale().
 * @return {number} Fraction 0–1.
 */
export function valueFraction( value, scale ) {
	const span = scale.max - scale.min;
	if ( ! span ) {
		return 0;
	}
	return clamp( ( toNumber( value ) - scale.min ) / span, 0, 1 );
}

/**
 * Where the zero line sits, as a fraction from the axis minimum.
 *
 * @param {Object} scale Scale from resolveScale().
 * @return {number} Fraction 0–1.
 */
export function baselineFraction( scale ) {
	return valueFraction( 0, scale );
}

/**
 * Centre of a category band, as a fraction of the plot width.
 *
 * Bands split the plot evenly, so a line point sits mid-band and lines up with
 * the column of bars and the category label under it.
 *
 * @param {number} index Category index.
 * @param {number} count Category count.
 * @return {number} Fraction 0–1.
 */
export function bandCenter( index, count ) {
	if ( count < 1 ) {
		return 0.5;
	}
	return ( index + 0.5 ) / count;
}

/**
 * Points for a line/area polyline in the 0–100 plot viewBox.
 *
 * The y axis is flipped: SVG grows downward, the value axis grows upward.
 *
 * @param {number[]} data  Series values.
 * @param {Object}   scale Scale from resolveScale().
 * @return {Array<{x: number, y: number, value: number, index: number}>} Points.
 */
export function linePoints( data, scale ) {
	const values = data || [];
	return values.map( ( value, index ) => ( {
		index,
		value: toNumber( value ),
		x: bandCenter( index, values.length ) * 100,
		y: 100 - valueFraction( value, scale ) * 100,
	} ) );
}

/**
 * A Catmull-Rom-to-bezier path through the points, for the smoothed line option.
 *
 * Tension is deliberately gentle: a smoothed line already invents values
 * between the points, and an aggressive curve invents overshoot too.
 *
 * @param {Array}   points Points from linePoints().
 * @param {boolean} smooth Whether to curve.
 * @return {string} SVG path data.
 */
export function linePath( points, smooth ) {
	if ( ! points.length ) {
		return '';
	}
	if ( points.length === 1 ) {
		return `M ${ fmt( points[ 0 ].x ) } ${ fmt( points[ 0 ].y ) }`;
	}
	if ( ! smooth ) {
		return points
			.map(
				( point, index ) =>
					`${ index ? 'L' : 'M' } ${ fmt( point.x ) } ${ fmt(
						point.y
					) }`
			)
			.join( ' ' );
	}

	let path = `M ${ fmt( points[ 0 ].x ) } ${ fmt( points[ 0 ].y ) }`;
	for ( let i = 0; i < points.length - 1; i++ ) {
		const p0 = points[ i - 1 ] || points[ i ];
		const p1 = points[ i ];
		const p2 = points[ i + 1 ];
		const p3 = points[ i + 2 ] || p2;
		const c1x = p1.x + ( p2.x - p0.x ) / 6;
		const c1y = p1.y + ( p2.y - p0.y ) / 6;
		const c2x = p2.x - ( p3.x - p1.x ) / 6;
		const c2y = p2.y - ( p3.y - p1.y ) / 6;
		path += ` C ${ fmt( c1x ) } ${ fmt( c1y ) } ${ fmt( c2x ) } ${ fmt(
			c2y
		) } ${ fmt( p2.x ) } ${ fmt( p2.y ) }`;
	}
	return path;
}

/**
 * Close a line path down to the baseline so it can be filled as an area.
 *
 * @param {string} path     Path from linePath().
 * @param {Array}  points   Points from linePoints().
 * @param {number} baseline Baseline y in viewBox units.
 * @return {string} SVG path data.
 */
export function areaPath( path, points, baseline ) {
	if ( ! path || ! points.length ) {
		return '';
	}
	const first = points[ 0 ];
	const last = points[ points.length - 1 ];
	return `${ path } L ${ fmt( last.x ) } ${ fmt( baseline ) } L ${ fmt(
		first.x
	) } ${ fmt( baseline ) } Z`;
}

/**
 * Coerce an attribute value to a finite number.
 *
 * @param {*} value Raw value.
 * @return {number} Number, 0 when unparseable.
 */
export function toNumber( value ) {
	const number = typeof value === 'number' ? value : parseFloat( value );
	return isFinite( number ) ? number : 0;
}

/**
 * Clamp to a range.
 *
 * @param {number} value Value.
 * @param {number} min   Lower bound.
 * @param {number} max   Upper bound.
 * @return {number} Clamped value.
 */
export function clamp( value, min, max ) {
	return Math.min( max, Math.max( min, value ) );
}

/**
 * Trim path coordinates to three decimals — beyond that is bytes, not precision.
 *
 * @param {number} value Coordinate.
 * @return {number} Trimmed coordinate.
 */
function fmt( value ) {
	return Math.round( value * 1000 ) / 1000;
}
